import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MapPin, Trash2, Star, Loader2, Eye, Plus } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useLocations } from "@/hooks/useLocations";
import { signInWithRedirect } from "aws-amplify/auth";

export default function Locations() {
  const { isAuthenticated, isLoading: authLoading, mockLogin } = useAuth();
  const [, navigate] = useLocation();
  const {
    locations,
    isLoading: locationsLoading,
    deleteLocation,
    setPrimaryLocation,
    isDeleting,
    isSettingPrimary,
  } = useLocations();

  const handleDelete = async (id: string) => {
    try {
      await deleteLocation(id);
      toast.success("Location removed");
    } catch (error) {
      toast.error("Failed to remove location");
      // Delete error (console removed)
    }
  };

  const handleSetPrimary = async (id: string) => {
    try {
      await setPrimaryLocation(id);
      toast.success("Primary location updated");
    } catch (error) {
      toast.error("Failed to update primary location");
      // Set primary error (console removed)
    }
  };

  const handleViewForecast = (location: { name: string; latitude: number; longitude: number }) => {
    navigate(`/forecast?lat=${location.latitude}&lon=${location.longitude}&name=${encodeURIComponent(location.name)}`);
  };

  if (authLoading || (isAuthenticated && locationsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" role="status" aria-live="polite">
        <Loader2 className="h-12 w-12 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">Loading locations...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4 card-elevated border-none shadow-premium">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg" aria-hidden="true">
              <MapPin className="h-8 w-8 text-white" />
            </div>
            <CardTitle asChild>
              <h2 className="text-2xl font-semibold">Sign In Required</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-muted-foreground text-lg">
              Sign in to manage your saved locations
            </p>
            <div className="space-y-3">
              <Button
                size="lg"
                onClick={() => signInWithRedirect()}
                className="w-full"
              >
                Sign In with AWS Cognito
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>
              <Button
                size="lg"
                variant="outline"
                onClick={() => mockLogin()}
                className="w-full"
              >
                Continue as Demo User
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Header with Background Image */}
      <section className="relative py-12 sm:py-16 md:py-24 lg:py-32 overflow-hidden" aria-label="Saved Locations hero section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img
            src="/hero-locations.webp"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background" />
        </div>

        <div className="container text-center relative z-10">
          <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
            <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl" aria-hidden="true">
              <MapPin className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white drop-shadow-lg">Saved Locations</h1>
            <p className="text-lg sm:text-xl text-white/90 drop-shadow-md">
              Manage your favorite locations for quick access to forecasts
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container py-8 sm:py-10 md:py-12">
        <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
          {/* Add Location Button */}
          <Button
            onClick={() => navigate("/forecast")}
            className="w-full"
            size="lg"
          >
            <Plus className="h-5 w-5 mr-2" aria-hidden="true" />
            Add New Location
          </Button>

          {/* Locations List */}
          <div aria-live="polite" aria-atomic="true">
            {locations.length === 0 ? (
              <Card className="card-elevated border-none shadow-premium">
                <CardContent className="p-12 text-center">
                  <MapPin className="h-16 w-16 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
                  <h2 className="text-2xl font-bold mb-2">No Saved Locations</h2>
                  <p className="text-muted-foreground mb-6">
                    Search for a location on the Forecast page to save it here
                  </p>
                  <Button onClick={() => navigate("/forecast")}>
                    Go to Forecast
                  </Button>
                </CardContent>
              </Card>
            ) : (
              locations.map((location) => (
                <Card key={location.id} className="card-elevated border-none shadow-premium">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg" aria-hidden="true">
                          <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base sm:text-lg font-semibold truncate">{location.name}</h3>
                            {location.isPrimary && (
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500 flex-shrink-0" aria-label="Primary location" />
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-auto sm:ml-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewForecast(location)}
                          aria-label={`View forecast for ${location.name}`}
                        >
                          <Eye className="h-4 w-4 mr-1" aria-hidden="true" />
                          View
                        </Button>
                        {!location.isPrimary && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetPrimary(location.id)}
                            disabled={isSettingPrimary}
                            aria-label={`Set ${location.name} as primary location`}
                          >
                            {isSettingPrimary ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <Star className="h-4 w-4" aria-hidden="true" />
                            )}
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isDeleting}
                              className="text-destructive hover:text-destructive"
                              aria-label={`Delete ${location.name}`}
                            >
                              {isDeleting ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              ) : (
                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Location</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{location.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(location.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
